import classnames from 'classnames';
import { isNaN } from 'lodash';
import { ActualTimes } from '../../../../../utils/constants/budget';
import {
  LineItemWithAmounts,
  Option,
  TimeFrame,
} from '../../../../../utils/customTypes';
import { ParentOfLineItems } from '../../../../../utils/types/budget';
import { mapRowColorWithLayer } from '../../../helpers/budgetHelper';

export const stickyCellsStyles = (className: string, layer: number) =>
  classnames(
    'border-transparent sticky z-1 ' + mapRowColorWithLayer(layer),
    className
  );

export const calculateTotalAnnualActualValue: (
  parentInfo: ParentOfLineItems
) => number = (parentInfo) => {
  // Recursive function to calculate total annual value
  const calculateAnnualValue = (parent: ParentOfLineItems): number => {
    // Calculate the sum of the parent's rawAmounts
    const parentSum = parent.rawActuals
      ? parent.rawActuals.reduce(
          (total: number, item: number) => total + (item ?? 0),
          0
        )
      : 0;

    // Recursively calculate the sum for children
    const childrenSum = parent.children
      ? parent.children.reduce(
          (total: number, child: ParentOfLineItems) =>
            total + calculateTotalAnnualActualValue(child),
          0
        )
      : 0;

    // Return total sum (parent + children)
    return parentSum + childrenSum;
  };

  return calculateAnnualValue(parentInfo);
};

export const calculateLineItemActualCell: (
  lineItemInfo: LineItemWithAmounts,
  selectedTime: Option,
  monthsForYearToDate: number[]
) => number = (lineItemInfo, selectedTime, monthsForYearToDate) => {
  let sum = 0;

  if (!isNaN(parseFloat(selectedTime.value)) && isFinite(selectedTime.value)) {
    sum = lineItemInfo.rawActuals[parseFloat(selectedTime.value)];
  } else if (selectedTime.value === ActualTimes.FISCAL_CURRENT) {
    sum = lineItemInfo.rawActuals.reduce(
      (total: number, item: number) => total + (item ?? 0),
      0
    );
  } else if (selectedTime.value === ActualTimes.FISCAL_NEXT) {
    //
  }
  return sum;
};

export const monthIndecesForYearToDate = (
  fiscalYear: number,
  fiscalMonthStart: number
): number[] => {
  const currentMonth = new Date().getUTCMonth();
  const currentYear = new Date().getUTCFullYear();
  const months = Array.from({ length: 12 }, (_, i) => i);
  let ans: number[] = [];

  if (fiscalYear === currentYear) {
    ans = months.filter(
      (month) => month <= currentMonth && month >= fiscalMonthStart
    );
  } else if (fiscalYear === currentYear - 1) {
    ans = months.filter(
      (month) => month <= currentMonth && month < fiscalMonthStart
    );
  }

  return ans;
};

export const calculateLineItemAllocationCell: (
  lineItemInfo: LineItemWithAmounts,
  selectedTime: Option
) => number = (lineItemInfo, selectedTime) => {
  let sum = 0;

  if (!isNaN(parseFloat(selectedTime.value)) && isFinite(selectedTime.value)) {
    return lineItemInfo.rawAmounts[parseFloat(selectedTime.value)];
  } else if (selectedTime.value === ActualTimes.FISCAL_CURRENT) {
    return lineItemInfo.rawAmounts.reduce(
      (total: number, item: number) => total + (item ?? 0),
      0
    );
  } else if (selectedTime.value === ActualTimes.FISCAL_NEXT) {
    //
  }

  return sum;
};

export const calculateParentActualCell: (
  parentInfo: ParentOfLineItems,
  timeframe: TimeFrame,
  fiscalMonthStart: number,
  selectedTime: Option,
  monthsForYearToDate: number[]
) => number = (
  parentInfo,
  timeframe,
  fiscalMonthStart,
  selectedTime,
  monthsForYearToDate
) => {
  let sum = 0;
  if (
    !isNaN(parseFloat(selectedTime.value)) &&
    isFinite(selectedTime.value) &&
    parentInfo.rawActuals
  ) {
    // selectedTime.value is already the calendar month index, no offset needed
    const index = parseFloat(selectedTime.value);
    sum += parentInfo.rawActuals[index];
  } else {
    if (parentInfo.lineItems && parentInfo.lineItems.length > 0) {
      for (const child of parentInfo.lineItems) {
        sum += calculateLineItemActualCell(
          child,
          selectedTime,
          monthsForYearToDate
        );
      }
    }
  }

  // Recursively add the raw amounts of children
  if (parentInfo.children && parentInfo.children.length > 0) {
    for (const child of parentInfo.children) {
      sum += calculateParentActualCell(
        child,
        timeframe,
        fiscalMonthStart,
        selectedTime,
        monthsForYearToDate
      );
    }
  }

  return sum;
};

export const calculateParentAllocationCell: (
  parentInfo: ParentOfLineItems,
  timeframe: TimeFrame,
  fiscalMonthStart: number,
  selectedTime: Option,
  monthsForYearToDate: number[]
) => number = (
  parentInfo,
  timeframe,
  fiscalMonthStart,
  selectedTime,
  monthsForYearToDate
) => {
  let sum = 0;

  if (
    !isNaN(parseFloat(selectedTime.value)) &&
    isFinite(selectedTime.value) &&
    parentInfo.rawAmounts
  ) {
    // selectedTime.value is already the calendar month index, no offset needed
    const index = parseFloat(selectedTime.value);
    sum += parentInfo.rawAmounts[index];
  } else {
    if (parentInfo.lineItems && parentInfo.lineItems.length > 0) {
      for (const child of parentInfo.lineItems) {
        sum += calculateLineItemAllocationCell(child, selectedTime);
      }
    }
  }

  // Recursively add the raw amounts of children
  if (parentInfo.children && parentInfo.children.length > 0) {
    for (const child of parentInfo.children) {
      sum += calculateParentAllocationCell(
        child,
        timeframe,
        fiscalMonthStart,
        selectedTime,
        monthsForYearToDate
      );
    }
  }

  return sum;
};
